import {asyncNoop} from '@benchling/js-utils';

import {
  addAndUpdateSteps,
  deleteAnalysisSteps,
  addStepToAnalysisWithInferredEdges,
} from 'analysis-tool/AnalysisActions';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {
  RightSidebarItemState,
  setRightSidebarItemIsUpdating,
  updateRightSidebarItemOnAddingStep,
  getStepDataToConnectNewTransformStep,
  getCurrentDataFrameSpec,
} from 'analysis-tool/header-options/DataFrameHeaderMenuUtil';
import getFilterColumnOptions, {
  DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP,
} from 'analysis-tool/header-options/getFilterColumnOptions';
import getHideColumnOptions from 'analysis-tool/header-options/getHideColumnOptions';
import getRenameColumnOptions from 'analysis-tool/header-options/getRenameColumnOptions';
import getRetypeColumnOptions from 'analysis-tool/header-options/getRetypeColumnOptions';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {
  ColumnHeaderTransformOptions,
  DataFrameHeaderMenuOptions,
} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import {getExistingHeaderMenuTransformStep, getReadOnlyInfo} from './DataFrameHeaderMenuUtil';

type GetTransformColumnOptions<StepType extends keyof ColumnHeaderTransformOptions> = (
  dataFrameSpec: DataFrameSpec,
  currentConfig: PipelineStepConfig<StepType, 0> | null,
  updateConfig: (newConfig: PipelineStepConfig<StepType, 0>) => Promise<void>,
  deleteStep: () => Promise<void>
) => ColumnHeaderTransformOptions[StepType];

/** Get header menu options for a supported column transform type. */
const GET_TRANSFORM_COLUMN_OPTIONS: {
  [StepType in keyof ColumnHeaderTransformOptions]: GetTransformColumnOptions<StepType>;
} = {
  [PipelineStepType.FILTER_DATASET]: getFilterColumnOptions,
  [PipelineStepType.HIDE_COLUMNS]: getHideColumnOptions,
  [PipelineStepType.RENAME_COLUMNS]: getRenameColumnOptions,
  [PipelineStepType.RETYPE_COLUMNS]: getRetypeColumnOptions,
};

/** Get placeholder menu options, for if an upstream step is running or failed. */
const GET_TRANSFORM_COLUMN_OPTIONS_FOR_IN_PROGRESS_STEP: {
  [StepType in keyof ColumnHeaderTransformOptions]: ColumnHeaderTransformOptions[StepType];
} = {
  [PipelineStepType.FILTER_DATASET]: DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP,
  [PipelineStepType.HIDE_COLUMNS]: {hideColumn: asyncNoop, disabledMessage: 'The analysis is still running.'},
  [PipelineStepType.RENAME_COLUMNS]: {renameColumn: asyncNoop},
  [PipelineStepType.RETYPE_COLUMNS]: {retypeColumn: asyncNoop},
};

/** Get header menu options for HIDE_COLUMNS, RENAME_COLUMNS, or RETYPE_COLUMNS. */
function getColumnHeaderTransformOptions<StepType extends keyof ColumnHeaderTransformOptions>(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  analysisClientStateId: string,
  rightSidebarItemState: RightSidebarItemState,
  stepType: StepType
): ColumnHeaderTransformOptions[StepType] {
  const existingTransformStep = getExistingHeaderMenuTransformStep(view, stepType);
  const dataFrameSpec = getCurrentDataFrameSpec(view, stepType);

  // Use placeholder/no-op options if any step in the view, except the filter step or its child, is not SUCCEEDED.
  const existingFilterStep = getExistingHeaderMenuTransformStep(view, PipelineStepType.FILTER_DATASET);
  if (
    view.lineage.some(
      ({step}) =>
        step.statusCode !== PipelineStepStatusCode.SUCCEEDED &&
        step.id !== existingFilterStep?.id &&
        !step.incomingEdges.some(({fromStep}) => fromStep.id === existingFilterStep?.id)
    )
  ) {
    return GET_TRANSFORM_COLUMN_OPTIONS_FOR_IN_PROGRESS_STEP[stepType];
  }

  if (existingTransformStep) {
    // Update the existing transform step
    const updateConfig = async (config: PipelineStepConfig<StepType, 0>): Promise<void> => {
      await withLoadingScreen(
        addAndUpdateSteps(analysis.id, {
          stepsToAdd: [],
          stepsToUpdate: [
            {
              stepId: existingTransformStep.id,
              config: config as unknown as Record<string, JSONValue>,
              rerunAfterUpdate: true,
            },
          ],
        })
      );
    };

    const deleteStep = async (): Promise<void> => {
      await deleteAnalysisSteps(
        analysis,
        {
          stepIds: [existingTransformStep.id],
          behavior: DeletePipelineStepsBehavior.NO_INFERENCE_WITH_REPARENTING,
        },
        analysisClientStateId
      );
    };

    return GET_TRANSFORM_COLUMN_OPTIONS[stepType](
      dataFrameSpec,
      existingTransformStep.config,
      updateConfig,
      deleteStep
    );
  } else {
    // Add a new transform step
    const stepDataInfo = getStepDataToConnectNewTransformStep(view);
    if (stepDataInfo == null) {
      return GET_TRANSFORM_COLUMN_OPTIONS_FOR_IN_PROGRESS_STEP[stepType];
    }
    const {stepData, stepDataIndex} = stepDataInfo;
    const updateConfig = async (config: PipelineStepConfig<StepType, 0>): Promise<void> => {
      setRightSidebarItemIsUpdating(rightSidebarItemState);

      const {stepId} = await withLoadingScreen(
        addStepToAnalysisWithInferredEdges(analysis, {
          stepType,
          stepData,
          stepDataIndex,
          config: config as unknown as Record<string, JSONValue>,
          runAfterCreate: true,
        })
      );

      const newStep = getNullableStepFromAnalysis(analysis, stepId);
      if (newStep) {
        updateRightSidebarItemOnAddingStep(
          newStep as unknown as GetAnalysisPipelineStep,
          rightSidebarItemState
        );
      }
    };

    const deleteStep = (): Promise<void> => Promise.resolve();

    return GET_TRANSFORM_COLUMN_OPTIONS[stepType](dataFrameSpec, null, updateConfig, deleteStep);
  }
}

/**
 * Get options for hiding, renaming, retyping and filtering data frame columns from column headers.
 */
export function getDataFrameHeaderMenuOptions(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  analysisClientStateId: string,
  rightSidebarItemState: RightSidebarItemState
): DataFrameHeaderMenuOptions {
  return {
    readOnlyInfo: getReadOnlyInfo(analysis, view),
    filterOptions: getColumnHeaderTransformOptions(
      analysis,
      view,
      analysisClientStateId,
      rightSidebarItemState,
      PipelineStepType.FILTER_DATASET
    ),
    hideColumnOptions: getColumnHeaderTransformOptions(
      analysis,
      view,
      analysisClientStateId,
      rightSidebarItemState,
      PipelineStepType.HIDE_COLUMNS
    ),
    renameColumnOptions: getColumnHeaderTransformOptions(
      analysis,
      view,
      analysisClientStateId,
      rightSidebarItemState,
      PipelineStepType.RENAME_COLUMNS
    ),
    retypeColumnOptions: getColumnHeaderTransformOptions(
      analysis,
      view,
      analysisClientStateId,
      rightSidebarItemState,
      PipelineStepType.RETYPE_COLUMNS
    ),
  };
}
